import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Users, Clock, CheckCircle2, XCircle, Eye, RefreshCw,
  AlertTriangle, FileImage, Loader2, ChevronRight, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminGetAadhaarQueue, adminBulkReviewAadhaar } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { canWrite } from "@/config/navigationConfig";

export default function AdminAadhaarQueue() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canReview = canWrite(user, 'verify_kyc');
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [working, setWorking] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [singleTarget, setSingleTarget] = useState(null); // {user_id, action} or null for bulk
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminGetAadhaarQueue();
      setQueue(res.data.queue || []);
      setSelected([]);
    } catch (e) {
      toast.error("Failed to load Aadhaar queue");
    } finally {
      setLoading(false);
    }
  };

  const allIds = useMemo(() => queue.map((q) => q.user_id), [queue]);
  const allSelected = selected.length === allIds.length && allIds.length > 0;

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected(allSelected ? [] : [...allIds]);

  const runReview = async (userIds, action, reason) => {
    if (!userIds || userIds.length === 0) return;
    setWorking(true);
    try {
      const res = await adminBulkReviewAadhaar(userIds, action, reason || null);
      const s = res.data.summary;
      if (s.errors > 0) {
        toast.warning(`${action === 'approve' ? 'Approved' : 'Rejected'} ${action === 'approve' ? s.approved : s.rejected}/${s.total_requested} — ${s.errors} failed`);
      } else {
        toast.success(`${action === 'approve' ? '✓ Approved' : '✓ Rejected'} ${action === 'approve' ? s.approved : s.rejected} Aadhaar upload${s.total_requested > 1 ? 's' : ''}`);
      }
      await load();
    } catch (e) {
      toast.error("Review failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setWorking(false);
      setShowRejectModal(false);
      setRejectReason("");
      setSingleTarget(null);
    }
  };

  const handleApproveSingle = async (userId) => {
    if (!window.confirm("Approve this customer's Aadhaar?")) return;
    await runReview([userId], 'approve');
  };

  const handleRejectSingle = (userId) => {
    setSingleTarget({ user_id: userId, action: 'reject' });
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleBulkApprove = async () => {
    if (!window.confirm(`Approve ${selected.length} Aadhaar upload(s)?`)) return;
    await runReview(selected, 'approve');
  };

  const handleBulkReject = () => {
    if (selected.length === 0) return;
    setSingleTarget(null); // bulk
    setRejectReason("");
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      toast.error("Please enter a rejection reason");
      return;
    }
    if (singleTarget) {
      await runReview([singleTarget.user_id], 'reject', reason);
    } else {
      await runReview(selected, 'reject', reason);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return d; }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-[3px] border-[#1565C0]/20 border-t-[#1565C0] rounded-full animate-spin" />
        <p className="text-sm text-[#6B7280]">Loading review queue...</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Outfit, sans-serif'}}>
            Aadhaar Review Queue
          </h2>
          <p className="text-sm text-[#6B7280] mt-1">
            Customers who have uploaded Aadhaar and are awaiting manual approval
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={load} disabled={working} variant="outline" className="rounded-xl">
            <RefreshCw className={`h-4 w-4 mr-2 ${working ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          {selected.length > 0 && canReview && (
            <>
              <Button
                onClick={handleBulkApprove}
                disabled={working}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl px-5 py-2.5 font-semibold shadow-sm hover:shadow-md transition-all"
                data-testid="bulk-approve-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" /> Approve {selected.length} Selected
              </Button>
              <Button
                onClick={handleBulkReject}
                disabled={working}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 rounded-xl"
                data-testid="bulk-reject-btn"
              >
                <XCircle className="h-4 w-4 mr-2" /> Reject {selected.length} Selected
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Pending Review</p>
              <p className="text-2xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>{queue.length}</p>
            </div>
            <div className="bg-amber-50 p-3 rounded-xl">
              <Clock className="h-6 w-6 text-amber-500" strokeWidth={1.8} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Selected</p>
              <p className="text-2xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>{selected.length}</p>
            </div>
            <div className="bg-blue-50 p-3 rounded-xl">
              <Users className="h-6 w-6 text-[#1565C0]" strokeWidth={1.8} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">PAN Also Verified</p>
              <p className="text-2xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>
                {queue.filter((q) => q.pan_verified).length}
              </p>
            </div>
            <div className="bg-emerald-50 p-3 rounded-xl">
              <ShieldCheck className="h-6 w-6 text-emerald-500" strokeWidth={1.8} />
            </div>
          </div>
        </div>
      </div>

      {/* Empty state */}
      {queue.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center" data-testid="aadhaar-queue-empty">
          <ShieldCheck className="h-16 w-16 mx-auto text-emerald-400 mb-4" strokeWidth={1.3} />
          <h3 className="text-xl font-black text-[#0A2540] mb-1" style={{fontFamily:'Outfit, sans-serif'}}>
            All caught up!
          </h3>
          <p className="text-sm text-[#6B7280]">No Aadhaar uploads are pending review right now.</p>
        </div>
      )}

      {/* Queue cards */}
      {queue.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Select-all toolbar */}
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="h-4 w-4 rounded border-gray-300 text-[#1565C0] focus:ring-[#1565C0]"
              data-testid="select-all-checkbox"
            />
            <span className="text-xs font-semibold text-[#4B5563]">
              {allSelected ? 'Deselect all' : 'Select all'} ({queue.length})
            </span>
          </div>

          <div className="divide-y divide-gray-100">
            {queue.map((item) => {
              const checked = selected.includes(item.user_id);
              return (
                <div
                  key={item.user_id}
                  className={`p-5 hover:bg-gray-50/60 transition-colors ${checked ? 'bg-blue-50/40' : ''}`}
                  data-testid={`aadhaar-row-${item.user_id}`}
                >
                  <div className="flex items-start gap-4">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(item.user_id)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-[#1565C0] focus:ring-[#1565C0]"
                      data-testid={`select-${item.user_id}`}
                    />

                    {/* Thumbnails */}
                    <div className="flex gap-2 flex-shrink-0">
                      {[
                        { label: 'Front', url: item.aadhaar_front_url },
                        { label: 'Back', url: item.aadhaar_back_url },
                      ].map((d) => (
                        <div
                          key={d.label}
                          onClick={() => d.url && setPreviewUrl(d.url)}
                          className={`w-20 h-20 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center overflow-hidden relative ${d.url ? 'cursor-pointer hover:ring-2 hover:ring-[#1565C0]' : ''}`}
                        >
                          {d.url ? (
                            <>
                              <img src={d.url} alt={d.label} className="w-full h-full object-cover" loading="lazy" />
                              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition flex items-end justify-center">
                                <span className="text-[9px] font-bold text-white bg-black/60 px-1.5 py-0.5 rounded-t w-full text-center">
                                  {d.label}
                                </span>
                              </div>
                            </>
                          ) : (
                            <FileImage className="h-6 w-6 text-gray-300" />
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-bold text-[#0A2540] truncate">
                          {item.name || 'Unnamed customer'}
                        </h3>
                        {item.pan_verified ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                            <ShieldCheck className="h-3 w-3" /> PAN verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            <AlertTriangle className="h-3 w-3" /> PAN pending
                          </span>
                        )}
                      </div>
                      <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-xs text-[#4B5563]">
                        <div><span className="text-[#6B7280]">Mobile:</span> <span className="font-semibold text-[#0A2540]">{item.mobile || '—'}</span></div>
                        <div><span className="text-[#6B7280]">Aadhaar:</span> <span className="font-mono font-semibold text-[#0A2540]">{item.aadhaar_number_masked || '—'}</span></div>
                        <div><span className="text-[#6B7280]">Uploaded:</span> <span className="font-semibold text-[#0A2540]">{formatDate(item.uploaded_at)}</span></div>
                      </div>

                      {/* OCR extracted data — auto-populated from the Aadhaar front image at upload time. */}
                      {(item.ocr_name || item.ocr_dob) && (
                        <div
                          className="mt-2 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs"
                          data-testid={`ocr-block-${item.user_id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-bold text-[#1565C0] uppercase tracking-wide text-[10px]">
                              OCR extracted (auto)
                            </span>
                            {item.ocr_confidence && (
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                  item.ocr_confidence === 'high'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : item.ocr_confidence === 'medium'
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                Confidence: {item.ocr_confidence}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 grid grid-cols-2 gap-x-6 gap-y-0.5 text-[11px] text-[#0A2540]">
                            <div>
                              <span className="text-[#6B7280]">Name:</span>{' '}
                              <span className="font-semibold" data-testid={`ocr-name-${item.user_id}`}>
                                {item.ocr_name || '—'}
                              </span>
                            </div>
                            <div>
                              <span className="text-[#6B7280]">DOB:</span>{' '}
                              <span className="font-semibold font-mono" data-testid={`ocr-dob-${item.user_id}`}>
                                {item.ocr_dob || '—'}
                              </span>
                            </div>
                          </div>
                          <p className="mt-1 text-[10px] text-[#6B7280] italic">
                            Compare with the uploaded image above. Approval is still a manual decision.
                          </p>
                        </div>
                      )}

                      {item.rejection_reason && (
                        <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-2 py-1">
                          Last rejection: {item.rejection_reason}
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => navigate(`/admin/user/${item.user_id}`)}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold text-[#4B5563] hover:bg-gray-100 transition-colors"
                        title="View full profile"
                      >
                        <Eye className="h-3.5 w-3.5" /> View
                      </button>
                      {canReview ? (
                        <>
                          <button
                            onClick={() => handleApproveSingle(item.user_id)}
                            disabled={working}
                            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold text-white bg-emerald-500 hover:bg-emerald-600 transition-colors disabled:opacity-50"
                            data-testid={`approve-${item.user_id}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                          </button>
                          <button
                            onClick={() => handleRejectSingle(item.user_id)}
                            disabled={working}
                            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-50"
                            data-testid={`reject-${item.user_id}`}
                          >
                            <XCircle className="h-3.5 w-3.5" /> Reject
                          </button>
                        </>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200"
                          title="You need the 'verify_kyc' permission to approve/reject KYC"
                          data-testid="kyc-no-perm-badge"
                        >
                          <Lock className="h-3 w-3" /> View-only
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Image preview modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4"
          onClick={() => setPreviewUrl(null)}
        >
          <img
            src={previewUrl}
            alt="Aadhaar preview"
            className="max-w-full max-h-full rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setPreviewUrl(null)}
            className="absolute top-4 right-4 bg-white/90 rounded-full p-2 hover:bg-white"
          >
            <XCircle className="h-6 w-6 text-gray-800" />
          </button>
        </div>
      )}

      {/* Reject reason modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-black text-[#0A2540] mb-1" style={{fontFamily:'Outfit, sans-serif'}}>
              Reject {singleTarget ? 'Aadhaar' : `${selected.length} Aadhaar upload${selected.length > 1 ? 's' : ''}`}
            </h3>
            <p className="text-xs text-[#6B7280] mb-4">
              The customer(s) will be notified and asked to re-upload.
            </p>
            <label className="text-xs font-semibold text-[#4B5563] mb-1 block">Reason (required)</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="e.g., Back side is blurry — please re-upload a clearer image"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1565C0]/30 focus:border-[#1565C0]"
              data-testid="reject-reason-input"
              autoFocus
            />
            <div className="flex gap-3 mt-4 justify-end">
              <Button
                variant="outline"
                onClick={() => { setShowRejectModal(false); setRejectReason(""); setSingleTarget(null); }}
                disabled={working}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmReject}
                disabled={working || !rejectReason.trim()}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="confirm-reject-btn"
              >
                {working ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                Confirm Reject
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
